import React, { Component } from "react";
import { Row, Col } from "react-bootstrap";
import api from "APP_ROOT/api";
import T from "prop-types";
import { dollars } from "LIB/formatters/currency";
import { displayInt, percent } from "LIB/formatters/number";
import { DataTable } from "MODULES/shared/components";
import moment from "moment";
import { Link } from "react-router-dom";
import withCurrentUser from "MODULES/session/decorators/withCurrentUser";
import { properCase } from "LIB/formatters/text";
import LocationsDropDown from "MODULES/Locations/components/LocationsDropDown";
import { API_BASE, MONTH } from "APP_ROOT/constants";
import SimpleDateRangePicker from "MODULES/shared/components/SimpleDateRangePicker/SimpleDateRangePicker";
import MonthlyStats from "./MonthlyStats";
import HeaderStats from "./HeaderStats";

function renderRow(r, currentUser) {
  return (
    <tr key={r.buyer}>
      <td>
        <Link to={`/purchases-report?buyer_id=${r.buyer_id}`}>{r.buyer}</Link>
      </td>
      <td className="text-right">{r.incoming_quotes_count}</td>
      <td className="text-right">{r.incoming_quotes_complete}</td>
      <td className="text-right">{r.pos_created_count}</td>
      <td className="text-right">{r.po_line_items_count}</td>
      <td className="text-right">
        {displayInt(r.iq_to_po_conversion_percent)} %
      </td>
      <td className="text-right">{dollars(r.total_purchases, 0)}</td>
      <td className="text-right">{dollars(r.total_lot_purchases, 0)}</td>
      <td className="text-right">{dollars(r.total_realized, 0)}</td>
      <td className="text-right">
        {percent(r.total_realized, r.total_purchases)}
      </td>
      {currentUser.tenant.tools_used.map(t => (
        <td className="text-right">{percent(r[t], r.total_tools_used)}</td>
      ))}
    </tr>
  );
}

class Table extends Component {
  constructor() {
    super();
    this.fetchMonthResults = this.fetchMonthResults.bind(this);
    this.fetchTodaysResults = this.fetchTodaysResults.bind(this);
    this.state = {
      monthResults: [],
      todaysResults: [],
      monthlyDataLoading: false,
      startDate: moment().startOf(MONTH),
      endDate: moment().endOf(MONTH)
    };
  }

  componentDidMount() {
    this.fetchTodaysResults();
    this.fetchMonthResults();
  }

  handleDateChange = (startDate, endDate) => {
    this.setState({
      endDate,
      monthlyDataLoading: true,
      selectedDate: startDate,
      startDate
    });

    if (startDate) {
      this.fetchMonthResults(startDate);
    } else {
      this.setState({
        monthResults: [],
        monthlyDataLoading: false
      });
    }
  };

  handleLocationChange = locationId => {
    this.setState({
      monthlyDataLoading: true,
      locationId
    });

    this.fetchMonthResults(this.state.selectedDate, locationId);
  };

  fetchTodaysResults() {
    const startTime = moment()
      .startOf("day")
      .format();
    const endTime = moment()
      .endOf("day")
      .format();

    Promise.all([
      api.reports.purchases.search(startTime, endTime),
      api.reports.purchases.searchPerLocation(startTime, endTime)
    ]).then(([res, perLocation]) => {
      this.setState({
        todaysResults: Object.assign(res.data, {
          perLocation: perLocation.data
        }),
        monthlyDataLoading: false
      });
    });
  }

  fetchMonthResults(selectedDate, locationId) {
    const { startDate, endDate } = this.state;

    Promise.all([
      api.reports.purchases.search(startDate, endDate, locationId),
      api.reports.purchases.searchPerLocation(startDate, endDate)
    ]).then(([search, perLocation]) => {
      const buyers = search.data.buyers;

      this.setState({
        monthResults: { ...search.data, buyers, perLocation: perLocation.data },
        monthlyDataLoading: false
      });
    });
  }

  reportStartTime(selectedDate) {
    return moment(selectedDate || this.state.selectedDate)
      .startOf("month")
      .format();
  }

  reportEndTime(selectedDate) {
    return (this.reportStartTime(selectedDate)
      ? moment(this.reportStartTime(selectedDate))
      : moment()
    )
      .endOf("month")
      .format();
  }

  render() {
    const {
      todaysResults,
      monthResults,
      monthlyDataLoading,
      selectedDate,
      startDate,
      endDate
    } = this.state;

    const { currentUser } = this.props;

    return (
      <React.Fragment>
        {todaysResults.buyers && (
          <HeaderStats
            purchasesData={todaysResults}
            refresh={this.fetchTodaysResults}
          />
        )}
        <Row>
          <Col>
            <div
              className="page-header monthly-purchases-header"
              style={{ padding: "10px, 30px" }}
            >
              <h1 style={{ marginTop: "7px" }}>
                <span className="header-content">
                  <span style={{ marginRight: "10px", verticalAlign: "super" }}>
                    {startDate && `${moment(startDate).format("MMMM DD YYYY")}`}
                    {endDate && `- ${moment(endDate).format("MMMM DD YYYY")}`}
                  </span>
                  <div style={{ display: "inline-block" }}>
                    <SimpleDateRangePicker
                      handleDateChange={this.handleDateChange}
                    />
                  </div>
                </span>
              </h1>
              <div style={{ display: "flex" }}>
                <LocationsDropDown handleChange={this.handleLocationChange} />
                {monthResults.buyers && (
                  <a
                    href={`${API_BASE}/monthly_purchases/download.xls?start_time=${this.reportStartTime()}&end_time=${this.reportEndTime()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <button
                      className={`btn btn-primary btn-hollow pull-right m-l-5`}
                      type="button"
                    >
                      <i className="fa fa-lg fa-cloud-download" />
                      &nbsp;&nbsp;Download Report
                    </button>
                  </a>
                )}
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <DataTable loading={monthlyDataLoading} loadingRows={40}>
              <DataTable.Head>
                <th>Name</th>
                <th className="text-right"># IQs </th>
                <th className="text-right"># IQ Lines </th>
                <th className="text-right"># POs</th>
                <th className="text-right"># PO Lines</th>
                <th className="text-right">IQ/PO Conv. %</th>
                <th className="text-right">Purchased</th>
                <th className="text-right">Lot Purch</th>
                <th className="text-right">Total Realized</th>
                <th className="text-right">% Realized</th>
                {currentUser.tenant.tools_used.map(t => (
                  <th className="text-right">{properCase(t)}</th>
                ))}
              </DataTable.Head>
              <DataTable.Body>
                {monthResults.buyers
                  ? monthResults.buyers.map(r => renderRow(r, currentUser))
                  : []}
              </DataTable.Body>
            </DataTable>
          </Col>
        </Row>
        {monthResults.buyers && (
          <MonthlyStats
            selectedDate={selectedDate}
            purchasesData={monthResults}
            refresh={this.fetchMonthResults}
          />
        )}
      </React.Fragment>
    );
  }
}

Table.propTypes = {
  currentUser: T.shape({})
};

export default withCurrentUser(Table);



// WEBPACK FOOTER //
// ./app/modules/PurchasesDashboard/Components/Table.jsx